import { classNames } from "primereact/utils";
import { Tag } from "../Tag/Tag";
import { IPlanRowViewModel } from "./models/planRowViewModel";
import styles from "./PlanRow.module.scss";
import { humanizePaymentType } from "../../../utilities/paymentUtility";
import { useNavigate } from "react-router-dom";
import { constructUserPlanHref } from "../../../infrastructure/routes/routePaths";
import { dateUtility } from "../../../infrastructure/utilities/dateUtility";
import { AppTooltip } from "../../tooltip/AppTooltip";
import { PriceDisplay } from "../../PriceDisplay/PriceDisplay";

interface IPlanRowProps {
  plan: IPlanRowViewModel;
}

export const PlanRow = ({ plan }: IPlanRowProps) => {
  const navigate = useNavigate();

  // A plan with no visits left is "completed" (приключен); a plan whose validity
  // period lapsed is "expired" (изтекъл). Unlimited plans can only expire.
  const isExhausted = plan.remainingVisits != null && plan.remainingVisits <= 0;

  const statusLabel = plan.isActive
    ? "Активен"
    : plan.isDeleted
      ? "Изтрит"
      : isExhausted
        ? "Приключен"
        : "Изтекъл";

  const statusColor = plan.isActive
    ? "#5dffb6"
    : plan.isDeleted
      ? "#ff6b7a"
      : "#ffb85d";

  const onRowClick = () => {
    navigate(constructUserPlanHref(plan.userPlanId));
  };

  const basePaymentTypeLabel = humanizePaymentType(plan.paymentType);

  // The currency is no longer appended here — the amount itself carries it.
  const paymentTypeLabel = basePaymentTypeLabel;
  return (
    <div className={styles.plan_row} onClick={onRowClick}>
      <div className={styles.plan_row_top}>
        <span className={styles.plan_row_top_title}>
          {plan.name}{" "}
          <span className={styles.plan_row_top_planId}>({plan.id})</span>
        </span>
        <Tag label={statusLabel} color={statusColor} />
      </div>
      <div className={styles.plan_row_subtitle}>
        ID: <span className={styles.plan_row_value}>{plan.userPlanId}</span>
        <AppTooltip
          content={
            "При зареждане на абонаментна карта за потребител се генерира уникален номер – това е стойността, показана тук. Този номер позволява да се проследи коя карта е използвана за кой клас. Ако отворите конкретен клас, в който потребител е отбелязан като присъствал с абонаментна карта, ще видите малък таг: ‘абонаментна карта (#xxx)’. Числото в скобите е именно този уникален номер. Така се осигурява проследимост. <br/><br/>Всеки абонаментен план също има свой уникален идентификатор, който се вижда в скоби до името на плана горе."
          }
          className={styles.plan_row_tooltip}
        />
      </div>
      <div
        className={classNames(styles.plan_row_subtitle, styles.plan_row_value)}
      >
        {dateUtility.formatDate(plan.createdOn)} - {""}
        {dateUtility.formatDate(plan.activeUntil)}
      </div>
      {plan.planType && (
        <div className={styles.plan_row_subtitle}>
          Тип: <span className={styles.plan_row_value}>{plan.planType}</span>
        </div>
      )}
      <div className={styles.plan_row_bottom}>
        <div className={styles.plan_row_bottom_left}>
          <div className={styles.plan_row_bottom_left_item}>
            <span className={styles.plan_row_bottom_left_item_title}>
              Платено:
            </span>
            <span
              className={classNames(
                styles.plan_row_bottom_left_item_value,
                styles.plan_row_bottom_left_item_price,
              )}
            >
              <PriceDisplay
                minPrice={plan.pricePaid}
                currencyId={plan.currencyId}
                stackOnMobile
              />
            </span>
          </div>
          <div className={styles.plan_row_bottom_left_item}>
            <span className={styles.plan_row_bottom_left_item_title}>НП:</span>
            <span
              className={classNames(styles.plan_row_bottom_left_item_value)}
            >
              {paymentTypeLabel}
            </span>
          </div>
        </div>
        <div className={styles.plan_row_bottom_right}>
          <span className={styles.plan_row_bottom_right_title}>
            Оставащи посещения:
          </span>
          <span className={styles.plan_row_bottom_right_value}>
            {plan.remainingVisits ?? "∞"}
          </span>
        </div>
      </div>
    </div>
  );
};
